import React, { useMemo } from 'react'
import { List } from 'antd'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import {
  Request,
  RequestQuoteHotel,
  RequestQuoteHotelRoom,
  RequestQuoteHotelRoomGuest,
  RequestTicket,
  RequestTicketHotelSelected,
  RequestTicketSelectedSituation
} from 'src/types'
import { Switch, Tooltip } from 'src/components/Ant'

import Item from './ticket-item'
import { WrapOptionCancel } from '../../styles'
import { useUpdatesPendingIssueTickets } from '../util'

type Props = {
  canEdit: boolean
  request: Request
}

export type SelectedHotel = Pick<
  RequestQuoteHotel,
  | 'address'
  | 'brand_name'
  | 'checkin_date'
  | 'checkout_date'
  | 'hotel_image_url'
> &
  Pick<
    RequestQuoteHotelRoom,
    | 'category'
    | 'guests'
    | 'integration_type'
    | 'pension'
    | 'request_quote_hotel_room_hash'
    | 'total_value'
  > &
  Pick<
    RequestTicketHotelSelected,
    'booking_code' | 'comments' | 'eticket_code'
  > & {
    checkin_date_formatted: string
    checkout_date_formatted: string
    situation: RequestTicketSelectedSituation
  } & Pick<
    RequestTicket,
    'request_ticket_id' | 'request_ticket_user_data' | 'status'
  >

function Hotels({ canEdit, request }: Props) {
  const { t } = useTranslation()
  const { isTicketsPending, handleChange } =
    useUpdatesPendingIssueTickets('HOTEL')
  const selectedHotels = useMemo<SelectedHotel[]>(() => {
    const items: SelectedHotel[] = []

    request.request_tickets
      .filter(
        requestTicketItem =>
          requestTicketItem.class_type === 'SERVICE' &&
          requestTicketItem.request_ticket_type === 'HOTEL'
        // && requestTicketItem.status === 'EMISSION_PENDING'
      )
      .forEach(requestTicketItem => {
        const {
          request_ticket_hotel_selected: {
            booking_code,
            comments,
            eticket_code,
            request_quote_hotel_room: {
              category,
              pension,
              request_quote_hotel_room_id,
              request_quote_hotel: {
                address,
                brand_name,
                checkin_date,
                checkout_date,
                hotel_image_url,
                request_quote: { request_quote_type }
              },
              total_value
            },
            situation
          },
          request_ticket_id,
          request_ticket_user_data,
          status
        } = requestTicketItem

        items.push({
          address,
          booking_code,
          brand_name,
          category,
          checkin_date,
          checkout_date,
          comments,
          hotel_image_url,
          integration_type: request_quote_type,
          pension,
          request_quote_hotel_room_hash: request_quote_hotel_room_id,
          request_ticket_id,
          request_ticket_user_data,
          situation,
          status,
          checkin_date_formatted: moment
            .utc(+checkin_date * 1000)
            .format('DD/MM/YYYY'),
          checkout_date_formatted: moment
            .utc(+checkout_date * 1000)
            .format('DD/MM/YYYY'),
          total_value,
          eticket_code,
          guests: request_ticket_user_data.map<RequestQuoteHotelRoomGuest>(
            ({ owner, traveler }) => ({
              owner,
              user_id: traveler.user_id
            })
          )
        })
      })

    return items
  }, [request.request_tickets])

  const canSendRequest = selectedHotels.some(
    ({ status }) => status === 'EMISSION_PENDING'
  )

  return (
    <>
      {canSendRequest && (
        <Tooltip title="web.agencies.cancel-tickets.cancel-hint">
          <WrapOptionCancel>
            <Switch
              style={{ width: '200px' }}
              disabled={false}
              unCheckedChildren={t(
                'web.agencies.cancel-tickets.cancel-unchecked'
              )}
              checkedChildren={t('web.agencies.cancel-tickets.cancel-checked')}
              checked={isTicketsPending}
              onChange={handleChange}
            />
          </WrapOptionCancel>
        </Tooltip>
      )}
      <List
        dataSource={selectedHotels}
        grid={{ gutter: 24, column: 2 }}
        itemLayout="vertical"
        renderItem={(item, index) => (
          <Item canEdit={canEdit} key={index} item={item} />
        )}
        style={{ margin: '0 24px' }}
      />
    </>
  )
}

export default Hotels
